import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateAuthorizedPickupInput,
  UpdateAuthorizedPickupInput,
} from "@shared/validation/authorizedPickup.schema";
import { PICKUP_CONSENT_ENABLED } from "@shared/constants/features";
import { apiClient, ApiError } from "./client";

export interface AuthorizedPickupSummary {
  id: string;
  childId: string;
  name: string;
  relationship: string | null;
  phone: string | null;
  /** Absent while PHOTO_UPLOADS_ENABLED is false: the server returns no images. */
  photoUrl?: string | null;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

const pickupsKey = (childId: string) =>
  ["children", childId, "authorized-pickups"] as const;

export function useAuthorizedPickups(childId: string | undefined) {
  return useQuery<AuthorizedPickupSummary[], ApiError>({
    queryKey: childId ? pickupsKey(childId) : ["children", "__none__", "authorized-pickups"],
    queryFn: () =>
      apiClient<AuthorizedPickupSummary[]>(`/api/children/${childId}/authorized-pickups`),
    // Off, the route is not mounted: gating here (as useMyOrgs does) stops any
    // page from firing a request that would 404. Consumers get `undefined`.
    enabled: PICKUP_CONSENT_ENABLED && Boolean(childId),
  });
}

export function useCreateAuthorizedPickup(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<AuthorizedPickupSummary, ApiError, CreateAuthorizedPickupInput>({
    mutationFn: (vars) =>
      apiClient<AuthorizedPickupSummary>(`/api/children/${childId}/authorized-pickups`, {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pickupsKey(childId) });
    },
  });
}

export function useUpdateAuthorizedPickup(childId: string, pickupId: string) {
  const queryClient = useQueryClient();
  return useMutation<AuthorizedPickupSummary, ApiError, UpdateAuthorizedPickupInput>({
    mutationFn: (vars) =>
      apiClient<AuthorizedPickupSummary>(
        `/api/children/${childId}/authorized-pickups/${pickupId}`,
        {
          method: "PUT",
          body: JSON.stringify(vars),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pickupsKey(childId) });
    },
  });
}

export function useDeleteAuthorizedPickup(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ deleted: true }, ApiError, string>({
    mutationFn: (pickupId) =>
      apiClient<{ deleted: true }>(
        `/api/children/${childId}/authorized-pickups/${pickupId}`,
        {
          method: "DELETE",
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pickupsKey(childId) });
    },
  });
}
